/**
 * Leviathan Web - Contract Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface ContractPrice {
  id: string;
  fixedPrice: number | null;
  rackPlusAmount: number | null;
  marginPercent: number | null;
  costPlusAmount: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  product: {
    id: string;
    sku: string;
    name: string;
    costPerUnit: number | null;
    pricePerUnit: number | null;
    unit: string | null;
  };
}

interface Contract {
  id: string;
  contractNumber: string;
  status: string;
  startDate: string;
  endDate: string;
  volumeCommitment: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  customer: {
    id: string;
    customerNumber: string;
    name: string;
    type: string;
    status: string;
  };
  prices: ContractPrice[];
  _count: {
    prices: number;
  };
}

const CONTRACT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400 border-gray-500' },
  ACTIVE: { label: 'Active', color: 'bg-success-green/20 text-success-green border-success-green' },
  EXPIRED: { label: 'Expired', color: 'bg-error-red/20 text-error-red border-error-red' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-500/20 text-gray-400 border-gray-500' },
  PENDING_RENEWAL: {
    label: 'Pending Renewal',
    color: 'bg-warning-amber/20 text-warning-amber border-warning-amber',
  },
};

const ContractDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [contract, setContract] = useState<Contract | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRenewing, setIsRenewing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [id]);

  const fetchContract = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setContract(data.contract);
      } else {
        navigate('/pricing/contracts');
      }
    } catch (error) {
      console.error('Failed to fetch contract:', error);
      navigate('/pricing/contracts');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        fetchContract();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleRenew = async () => {
    setIsRenewing(true);
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}/renew`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ durationMonths: 12 }),
      });
      if (response.ok) {
        const data = await response.json();
        navigate(`/pricing/contracts/${data.contract.id}`);
      }
    } catch (error) {
      console.error('Failed to renew contract:', error);
    } finally {
      setIsRenewing(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        navigate('/pricing/contracts');
      }
    } catch (error) {
      console.error('Failed to delete contract:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return `$${price.toFixed(4)}`;
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getEffectivePrice = (entry: ContractPrice): string => {
    if (entry.fixedPrice !== null) return formatPrice(entry.fixedPrice);
    if (entry.rackPlusAmount !== null) return `Rack + ${formatPrice(entry.rackPlusAmount)}`;
    if (entry.marginPercent !== null) return `${entry.marginPercent}% margin`;
    if (entry.costPlusAmount !== null) return `Cost + ${formatPrice(entry.costPlusAmount)}`;
    return '-';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading contract...
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <div className="text-text-tertiary font-mono text-lg mb-4">
          &gt; Contract not found
        </div>
        <Link
          to="/pricing/contracts"
          className="text-amber hover:text-amber/80 font-mono"
        >
          ← Back to Contracts
        </Link>
      </div>
    );
  }

  const statusStyle = CONTRACT_STATUS_LABELS[contract.status] || {
    label: contract.status,
    color: 'bg-gray-500/20 text-gray-400 border-gray-500',
  };
  const daysRemaining = getDaysRemaining(contract.endDate);
  const isExpiringSoon = contract.status === 'ACTIVE' && daysRemaining <= 30 && daysRemaining > 0;
  const isExpired = contract.status === 'ACTIVE' && daysRemaining <= 0;

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <Link
                to="/pricing/contracts"
                className="text-text-tertiary hover:text-amber font-mono text-sm mb-2 inline-block"
              >
                ← Back to Contracts
              </Link>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {contract.contractNumber}
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <span className={`px-2 py-0.5 text-xs font-mono border ${statusStyle.color}`}>
                  {statusStyle.label}
                </span>
                {isExpiringSoon && (
                  <span className="text-warning-amber font-mono text-sm">
                    ⚠️ Expires in {daysRemaining} days
                  </span>
                )}
                {isExpired && (
                  <span className="text-error-red font-mono text-sm">
                    ⚠️ Contract expired
                  </span>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              {contract.status === 'ACTIVE' && (
                <button
                  onClick={handleRenew}
                  disabled={isRenewing}
                  className="border border-steel text-steel hover:bg-steel/10 font-display font-bold py-2 px-4 transition-all uppercase tracking-wider text-sm disabled:opacity-50"
                >
                  {isRenewing ? 'Renewing...' : 'Renew Contract'}
                </button>
              )}
              <Link
                to={`/pricing/contracts/${id}/edit`}
                className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-2 px-4 transition-all uppercase tracking-wider text-sm"
              >
                Edit Contract
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contract Details */}
        <GeometricPanel className="lg:col-span-2">
          <h2 className="text-lg font-display text-steel uppercase tracking-wider mb-4">
            Contract Details
          </h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-1">
                Customer
              </label>
              <Link
                to={`/customers/${contract.customer.id}`}
                className="text-amber hover:text-amber/80 font-mono"
              >
                {contract.customer.name}
              </Link>
              <div className="text-text-tertiary text-xs font-mono">
                #{contract.customer.customerNumber}
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-1">
                Contract Number
              </label>
              <div className="text-text-primary font-mono">{contract.contractNumber}</div>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-1">
                Start Date
              </label>
              <div className="text-text-primary font-mono">{formatDate(contract.startDate)}</div>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-1">
                End Date
              </label>
              <div className="text-text-primary font-mono">{formatDate(contract.endDate)}</div>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-1">
                Volume Commitment
              </label>
              <div className="text-text-primary font-mono">
                {contract.volumeCommitment
                  ? `${contract.volumeCommitment.toLocaleString()} gallons`
                  : '-'}
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-1">
                Products
              </label>
              <div className="text-amber font-mono">{contract._count.prices}</div>
            </div>
          </div>
          {contract.notes && (
            <div className="mt-6 pt-4 border-t border-panel-border">
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Notes
              </label>
              <div className="text-text-primary font-mono text-sm whitespace-pre-wrap">
                {contract.notes}
              </div>
            </div>
          )}
        </GeometricPanel>

        {/* Quick Actions */}
        <GeometricPanel>
          <h2 className="text-lg font-display text-steel uppercase tracking-wider mb-4">
            Actions
          </h2>
          <div className="space-y-3">
            {contract.status === 'DRAFT' && (
              <button
                onClick={() => handleStatusChange('ACTIVE')}
                className="w-full bg-success-green/20 text-success-green border border-success-green hover:bg-success-green/30 font-mono py-2 px-4 transition-all uppercase text-sm"
              >
                Activate Contract
              </button>
            )}
            {contract.status === 'ACTIVE' && (
              <>
                <button
                  onClick={() => handleStatusChange('PENDING_RENEWAL')}
                  className="w-full bg-warning-amber/20 text-warning-amber border border-warning-amber hover:bg-warning-amber/30 font-mono py-2 px-4 transition-all uppercase text-sm"
                >
                  Mark Pending Renewal
                </button>
                <button
                  onClick={() => handleStatusChange('CANCELLED')}
                  className="w-full bg-error-red/20 text-error-red border border-error-red hover:bg-error-red/30 font-mono py-2 px-4 transition-all uppercase text-sm"
                >
                  Cancel Contract
                </button>
              </>
            )}
            {contract.status === 'PENDING_RENEWAL' && (
              <button
                onClick={handleRenew}
                disabled={isRenewing}
                className="w-full bg-amber/20 text-amber border border-amber hover:bg-amber/30 font-mono py-2 px-4 transition-all uppercase text-sm disabled:opacity-50"
              >
                {isRenewing ? 'Processing...' : 'Create Renewal'}
              </button>
            )}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full border border-panel-border text-text-secondary hover:text-error-red hover:border-error-red font-mono py-2 px-4 transition-all uppercase text-sm"
            >
              Delete Contract
            </button>
          </div>

          {/* Timeline */}
          <div className="mt-6 pt-4 border-t border-panel-border">
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-3">
              Timeline
            </label>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-text-tertiary font-mono">Created</span>
                <span className="text-text-secondary font-mono">
                  {formatDate(contract.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-tertiary font-mono">Updated</span>
                <span className="text-text-secondary font-mono">
                  {formatDate(contract.updatedAt)}
                </span>
              </div>
              {contract.status === 'ACTIVE' && (
                <div className="flex justify-between">
                  <span className="text-text-tertiary font-mono">Days Remaining</span>
                  <span
                    className={`font-mono ${
                      daysRemaining <= 30
                        ? 'text-error-red'
                        : daysRemaining <= 90
                          ? 'text-warning-amber'
                          : 'text-success-green'
                    }`}
                  >
                    {daysRemaining}
                  </span>
                </div>
              )}
            </div>
          </div>
        </GeometricPanel>
      </div>

      {/* Contract Prices */}
      <GeometricPanel>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-display text-steel uppercase tracking-wider">
            Contract Prices
          </h2>
          <Link
            to={`/pricing/contracts/${id}/edit`}
            className="text-amber hover:text-amber/80 font-mono text-sm"
          >
            Edit Prices →
          </Link>
        </div>

        {contract.prices.length === 0 ? (
          <div className="text-center py-8 text-text-tertiary font-mono">
            No products added to this contract yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-panel-border">
                  <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Product
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Default Price
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Contract Price
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Min
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Max
                  </th>
                </tr>
              </thead>
              <tbody>
                {contract.prices.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-panel-border/50 hover:bg-panel-dark/50"
                  >
                    <td className="py-3 px-4">
                      <div className="font-mono text-text-primary">{entry.product.name}</div>
                      <div className="text-text-tertiary text-xs">
                        {entry.product.sku}
                        {entry.product.unit && ` • ${entry.product.unit}`}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-text-secondary">
                      {formatPrice(entry.product.pricePerUnit)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-mono text-amber">{getEffectivePrice(entry)}</span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-text-tertiary">
                      {formatPrice(entry.minPrice)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-text-tertiary">
                      {formatPrice(entry.maxPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GeometricPanel>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative max-w-md w-full mx-4">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red p-6">
              <h3 className="text-xl font-display text-error-red uppercase tracking-wider mb-4">
                Delete Contract
              </h3>
              <p className="text-text-secondary font-mono mb-6">
                Are you sure you want to delete contract{' '}
                <span className="text-amber">{contract.contractNumber}</span>? This action cannot
                be undone.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-6 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors font-mono uppercase text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="bg-error-red hover:bg-error-red/90 text-white font-display font-bold py-2 px-6 transition-all uppercase tracking-wider text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractDetailPage;
